import app from "../../../../global-account/app.js";
import { useAuthStore } from "../../../../global-account/react/index.js";
import { B3_AUTH_COOKIE_NAME } from "../../../../shared/constants/index.js";
import { debugB3React } from "../../../../shared/utils/debug.js";
import Cookies from "js-cookie";
import { useEffect, useRef } from "react";
import { getBetterAuthClient } from "../../../better-auth-client.js";
import { useUserQuery } from "../../hooks/useUserQuery.js";
const debug = debugB3React("BetterAuthProvider");
/**
 * Parallel to AuthenticationProvider for Better Auth strategy.
 *
 * Manages the isAuthenticating lifecycle for Better Auth:
 * 1. On mount, try to restore an existing Feathers JWT (from a previous login)
 * 2. If no Feathers JWT, check for a Better Auth session (e.g. after OAuth redirect)
 *    and exchange it for a Feathers JWT
 * 3. If neither exists, set isAuthenticating: false so the login UI renders
 *
 * Also patches app.logout() so any code path that calls it (useAuthentication,
 * useAuth, SignIn component, etc.) automatically clears the Better Auth session.
 */
const BetterAuthProvider = ({
  partnerId
}) => {
  const setIsAuthenticated = useAuthStore(state => state.setIsAuthenticated);
  const setIsAuthenticating = useAuthStore(state_0 => state_0.setIsAuthenticating);
  const setIsConnected = useAuthStore(state_1 => state_1.setIsConnected);
  const {
    setUser
  } = useUserQuery();
  const hasAttemptedRestore = useRef(false);
  const hasPatched = useRef(false);
  // Patch app.logout() to also clear the Better Auth session.
  // This ensures any existing logout path (useAuthentication, useAuth, SignIn
  // dropdown, etc.) clears both the Feathers JWT and the Better Auth session.
  // Patch app.logout() to also clear the Better Auth session.
  // Only handles Better Auth signOut — state cleanup (isAuthenticated, isConnected,
  // setUser, localStorage) is handled by useAuthentication/useAuth's own logout.
  useEffect(() => {
    if (hasPatched.current) return;
    hasPatched.current = true;
    const originalLogout = app.logout.bind(app);
    app.logout = async () => {
      debug("Patched logout: clearing Better Auth session");
      try {
        await getBetterAuthClient().signOut();
      } catch {
        debug("Better Auth signOut failed (non-critical)");
      }
      return originalLogout();
    };
    return () => {
      app.logout = originalLogout;
      hasPatched.current = false;
    };
  }, []);
  // Session restore on mount
  useEffect(() => {
    if (hasAttemptedRestore.current) return;
    hasAttemptedRestore.current = true;
    const restoreSession = async () => {
      debug("Attempting session restore");
      // 1. Try existing Feathers JWT first (fastest — no network call to Better Auth)
      try {
        const response = await app.reAuthenticate();
        if (response?.user) {
          debug("Feathers JWT restored", {
            userId: response.user.userId
          });
          setUser(response.user);
          setIsAuthenticated(true);
          setIsConnected(true);
          setIsAuthenticating(false);
          return;
        }
      } catch {
        debug("No existing Feathers JWT");
      }
      // 2. Check for _ba_token in URL (OAuth callback with third-party cookie bypass).
      //    When the API and frontend are on different domains, browsers with third-party
      //    cookie blocking (Firefox TCP, Safari ITP, Chrome Privacy Sandbox) prevent the
      //    better-auth.session_token cookie from being sent on cross-origin requests.
      //    The B3 API injects the session token into the redirect URL as _ba_token,
      //    allowing direct exchange without relying on cookies.
      const params = new URLSearchParams(window.location.search);
      const baToken = params.get("_ba_token");
      if (baToken) {
        debug("Found _ba_token in URL, exchanging for Feathers JWT");
        // Clean the token from the URL immediately (security)
        params.delete("_ba_token");
        const cleanSearch = params.toString();
        const cleanUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : "") + window.location.hash;
        window.history.replaceState({}, "", cleanUrl);
        try {
          const response_0 = await app.authenticate({
            strategy: "better-auth",
            accessToken: baToken,
            partnerId
          });
          if (response_0.accessToken) {
            Cookies.set(B3_AUTH_COOKIE_NAME, response_0.accessToken, {
              secure: true,
              sameSite: "Lax"
            });
          }
          if (response_0.user) {
            setUser(response_0.user);
            setIsAuthenticated(true);
            setIsConnected(true);
          }
          debug("_ba_token exchanged for Feathers JWT", {
            userId: response_0.user?.userId
          });
          setIsAuthenticating(false);
          return;
        } catch (err) {
          debug("_ba_token exchange failed", err);
        }
      }
      // 3. Check for a Better Auth session via cookie (works when API and frontend
      //    share a domain or when cookies aren't partitioned)
      try {
        const session = await getBetterAuthClient().getSession();
        if (session.data?.session?.token) {
          debug("Better Auth session found, exchanging for Feathers JWT", {
            betterAuthUserId: session.data.user?.id
          });
          const response_1 = await app.authenticate({
            strategy: "better-auth",
            accessToken: session.data.session.token,
            partnerId
          });
          if (response_1.accessToken) {
            Cookies.set(B3_AUTH_COOKIE_NAME, response_1.accessToken, {
              secure: true,
              sameSite: "Lax"
            });
          }
          if (response_1.user) {
            setUser(response_1.user);
            setIsAuthenticated(true);
            setIsConnected(true);
          }
          debug("OAuth session exchanged for Feathers JWT", {
            userId: response_1.user?.userId
          });
          setIsAuthenticating(false);
          return;
        }
      } catch {
        debug("No Better Auth session to restore");
      }
      // 4. Nothing found — show login UI
      setIsAuthenticating(false);
    };
    restoreSession();
  }, [setIsAuthenticated, setIsAuthenticating, setIsConnected, setUser, partnerId]);
  return null;
};
export default BetterAuthProvider;